import { useEffect, useMemo, useState } from 'react';
import { Table } from '@tanstack/react-table';

import { NormalizedAgreement } from '@/types/file-explorer-types';

import IconAddComment from '@/assets/svg/icon-add-comment.svg?react';
import IconAddTag from '@/assets/svg/icon-add-tag-select.svg?react';
import IconChangeResponsible from '@/assets/svg/icon-change-author.svg?react';
import IconStatus from '@/assets/svg/icon-change-status.svg?react';

import { toast } from '@/hooks/use-toast';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { USER_ROLES } from '@/constants/global-constants';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { UserSelect } from '../ui/user-select';
import SelectionToolbar from './SelectionToolbar';

interface AgreementSelectionBarProps {
  table: Table<NormalizedAgreement>;
}
const AgreementSelectionBar = ({ table }: AgreementSelectionBarProps) => {
  const { user } = useAuthStore();
  const [selectValues, setSelectValues] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState<
    null | 'status' | 'tag' | 'comment' | 'responsible'
  >(null);
  const { updateAgreements } = useApiManagerStore();
  const { getAgreementsState } = useFileExplorerStore();

  const handleChangeStatus = async () => {
    setIsLoading(true);
    const selectedRows = table.getSelectedRowModel().rows;

    const agreementsToUpdate = selectedRows.map((row) => ({
      ...row.original,
      step: selectedValue,
    }));

    if (agreementsToUpdate.length === 0) return;
    await updateAgreements(agreementsToUpdate);

    getAgreementsState();
    setOpenDialog(null);
    setIsLoading(false);
  };

  const handleAddTag = async () => {
    setIsLoading(true);
    const selectedRows = table.getSelectedRowModel().rows;

    // Prepare payload: only agreements that don't already have the tag
    const agreementsToUpdate = selectedRows
      .filter((row) => !row.original.tag_set.includes(selectedValue))
      .map((row) => ({
        ...row.original,
        tag_set: [...row.original.tag_set, selectedValue],
      }));

    if (agreementsToUpdate.length === 0) return;

    await updateAgreements(agreementsToUpdate);

    getAgreementsState();
    setOpenDialog(null);
    setIsLoading(false);
  };

  const handleAddComment = async () => {
    setIsLoading(true);
    const selectedRows = table.getSelectedRowModel().rows;

    const promises = selectedRows.map(async (row) => {
      const newComment = await apiService.createComment({
        content: selectedValue,
        agreement: row.original.uuid,
        user: user?.uuid!,
        is_edited: false,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        is_system_comment: false,
      });
      return newComment;
    });
    const newComments = await Promise.all(promises);

    const agreementsToUpdate = selectedRows.map((row) => ({
      ...row.original,
      comments: [
        ...(row.original.comments || []),
        ...newComments.map((c) => c.uuid),
      ],
    }));

    if (agreementsToUpdate.length === 0) return;

    await updateAgreements(agreementsToUpdate);

    getAgreementsState();
    setOpenDialog(null);
    setIsLoading(false);
  };

  const handleChangeResponsible = async () => {
    setIsLoading(true);
    const selectedRows = table.getSelectedRowModel().rows;

    const agreementsToUpdate = selectedRows.map((row) => ({
      ...row.original,
      assignee: selectedValue,
    }));

    if (agreementsToUpdate.length === 0) return;
    await updateAgreements(agreementsToUpdate);

    getAgreementsState();
    setOpenDialog(null);
    setIsLoading(false);
  };

  const isDisabledChangeStatus = useMemo(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const isUniqueStatusForAllSelectedRows = selectedRows.every(
      (row) => row.original.process === selectedRows[0].original.process,
    );
    return !isUniqueStatusForAllSelectedRows;
  }, [table.getSelectedRowModel().rows.length]);

  useEffect(() => {
    if (openDialog !== 'status') return;
    const selectedProcess =
      table.getSelectedRowModel().rows[0].original.process;
    const processes = useGlobalStore.getState().processes;
    const { user } = useAuthStore.getState();
    const steps = useGlobalStore
      .getState()
      .steps.filter((step) => step.user_type.includes(user?.role ?? ''));

    const selectedProcesses = processes.find(
      (process) => process.uuid === selectedProcess,
    );
    if (!selectedProcesses) return;
    setSelectValues(
      selectedProcesses.steps
        .filter((step) => steps.find((s) => s.uuid === step))
        .map((step) => ({
          label: steps.find((s) => s.uuid === step)?.status_name ?? '',
          value: step,
        })),
    );
  }, [openDialog]);

  useEffect(() => {
    if (openDialog !== 'tag') return;
    const allTags = useGlobalStore.getState().allTags;
    setSelectValues(
      Object.values(allTags.entities).map((t) => ({
        label: t.name,
        value: t.uuid,
      })),
    );
  }, [openDialog]);

  const usersUuidsFromAgreement = useMemo(() => {
    return table.getSelectedRowModel().rows.reduce((acc, row) => {
      if (row.original.assignee) {
        acc.push(row.original.assignee);
      }

      if (row.original.creator) {
        acc.push(row.original.creator);
      }

      return acc;
    }, [] as string[]);
  }, [table.getSelectedRowModel().rows]);

  return (
    <>
      <SelectionToolbar
        selectedCount={table.getSelectedRowModel().rows.length}
        onClearSelection={() => table.resetRowSelection()}
        actions={
          <>
            {isDisabledChangeStatus ? (
              <button
                className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  toast({
                    title:
                      'Не можна змінити статус для декількох договорів, якщо процес різний',
                    variant: 'destructive',
                  });
                }}
              >
                <IconStatus className="h-3 w-3 min-w-3" />
                <span className="whitespace-nowrap font-medium">
                  Змінити статус
                </span>
              </button>
            ) : (
              <button
                className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setOpenDialog('status')}
              >
                <IconStatus className="h-3 w-3 min-w-3" />
                <span className="whitespace-nowrap font-medium">
                  Змінити статус
                </span>
              </button>
            )}
            <button
              className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white"
              onClick={() => setOpenDialog('tag')}
            >
              <IconAddTag className="h-4 w-4 min-w-4" />
              <span className="whitespace-nowrap font-medium">
                Додати мітку
              </span>
            </button>
            <button
              className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white"
              onClick={() => setOpenDialog('comment')}
            >
              <IconAddComment className="h-5 w-5 min-w-5" />
              <span className="whitespace-nowrap font-medium">
                Додати коментар
              </span>
            </button>

            {user?.role === USER_ROLES.CONTROLLER.value && (
              <button
                className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white"
                onClick={() => setOpenDialog('responsible')}
              >
                <IconChangeResponsible className="h-4 w-4 min-w-4" />
                <span className="whitespace-nowrap font-medium">
                  Змінити відповідального
                </span>
              </button>
            )}
          </>
        }
      />
      <Dialog
        open={openDialog === 'status'}
        onOpenChange={(isOpen) => setOpenDialog(isOpen ? 'status' : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-4 text-2xl font-semibold">
              Змінити статус
            </DialogTitle>
            <DialogDescription>
              <Select onValueChange={(value) => setSelectedValue(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Виберіть статус" />
                </SelectTrigger>
                <SelectContent>
                  {selectValues.map((selected) => (
                    <SelectItem key={selected.value} value={selected.value}>
                      {selected.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col px-0 md:justify-end">
            <Button
              variant="outline"
              onClick={handleChangeStatus}
              disabled={isLoading}
            >
              {isLoading ? 'працює...' : 'Зберегти'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog === 'tag'}
        onOpenChange={(isOpen) => setOpenDialog(isOpen ? 'tag' : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-4 text-2xl font-semibold text-primary">
              Додати мітку
            </DialogTitle>
            <DialogDescription>
              <Select onValueChange={(value) => setSelectedValue(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Виберіть мітку" />
                </SelectTrigger>
                <SelectContent>
                  {selectValues.map((selected) => (
                    <SelectItem key={selected.value} value={selected.value}>
                      {selected.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col px-0 md:justify-end">
            <Button
              variant="outline"
              onClick={handleAddTag}
              disabled={isLoading}
            >
              {isLoading ? 'працює...' : 'Зберегти'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog === 'comment'}
        onOpenChange={(isOpen) => setOpenDialog(isOpen ? 'comment' : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-4 text-2xl font-semibold text-primary">
              Додати коментар
            </DialogTitle>
            <DialogDescription>
              <Textarea
                onChange={(e) => setSelectedValue(e.target.value.trim())}
                placeholder="Введіть коментар"
                maxLength={4000}
                className="min-h-[300px] resize-none rounded-md"
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col px-0 md:justify-end">
            <Button
              variant="outline"
              onClick={handleAddComment}
              disabled={isLoading}
            >
              {isLoading ? 'працює...' : 'Зберегти'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog === 'responsible'}
        onOpenChange={(isOpen) => setOpenDialog(isOpen ? 'responsible' : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-4 text-2xl font-semibold text-primary">
              Змінити відповідального
            </DialogTitle>
            <DialogDescription>
              <UserSelect
                onValueChange={(value) => setSelectedValue(value)}
                usersUuidsFromAgreement={usersUuidsFromAgreement}
              >
                <SelectTrigger className="relative w-full">
                  <SelectValue placeholder="Виберіть користувача..." />
                  {/* {isLoading && (
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 transform">
                      <div className="h-4 w-4 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                    </div>
                  )} */}
                </SelectTrigger>
              </UserSelect>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col px-0 md:justify-end">
            <Button
              variant="outline"
              onClick={handleChangeResponsible}
              disabled={isLoading}
            >
              {isLoading ? 'працює...' : 'Зберегти'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgreementSelectionBar;
